/**
 * Local margin-estimate math — Phase 6a-8 commit 21 of the decomposition
 * (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6, v2.19/R34-extended R13
 * signatures). Bodies moved from static/paper.html with the two impurities —
 * `state.underlyingLtp` and the IST clock — made explicit parameters; the
 * page keeps thin wrappers with the historical names and call shapes.
 *
 * BACKEND TWIN: routers/margin.py (_local_margin_estimate /
 * _is_expiry_elm_active / _apply_expiry_elm). The BUSINESS CONSTANTS below
 * are synchronized with that file — keep both in sync. Two structural
 * differences are INTENTIONAL, documented, and must not be "corrected":
 *   1. Underlying sourcing — the backend reads a PER-LEG
 *      leg["underlying_ltp"]; this frontend estimate uses the single live
 *      `underlyingLtp` passed in (all legs of one basket share one
 *      underlying here).
 *   2. ELM placement — the backend applies the expiry ELM CENTRALLY
 *      (_apply_expiry_elm, once, to the total from any source, so the live
 *      Fyers path gets it too and nothing double-counts); this frontend
 *      fallback adds it PER SHORT LEG inline, which is equivalent for the
 *      local-estimate path this code serves.
 *
 * These are rough LOCAL-ONLY fallbacks: real SPAN+Exposure comes from the
 * live /api/margin/basket call; this math is the bootstrap value before the
 * first refresh and the offline safety net when that call fails.
 *
 * Pure module: explicit inputs only. The optional `at` clock parameter
 * defaults PER INVOCATION to nowIST() (the isMarketHoursNow convention) —
 * never cached at module scope — so wrapper-less page behaviour is
 * byte-equivalent while unit tests stay deterministic.
 */

import { dateStrFromLocalDate, nowIST, previousTradingDay } from "../market_time";

// Synchronized with routers/margin.py (SPAN_ESTIMATE_PCT etc.) — rough,
// deliberately conservative approximations of real NSE SPAN+Exposure
// behavior for index options, used only when the live margin call fails.
export const SPAN_ESTIMATE_PCT = 0.10;     // SPAN ballpark: ~10% of notional
export const EXPOSURE_ESTIMATE_PCT = 0.03; // Exposure ballpark: ~3% of notional
export const HEDGE_HAIRCUT_ESTIMATE = 0.70; // same-underlying/expiry long cuts the naked estimate ~70%
export const SOMM_FLOOR_PCT_ESTIMATE = 0.20; // Short Option Minimum Margin floor: ≥20% of naked

// Deliberately huge placeholder — NOT a calculated margin — returned whenever
// the estimate has no reliable underlying index price to work from. Reported
// bug: when the underlying was unavailable, the estimate silently substituted
// the OPTION'S OWN PREMIUM (e.g. ~₹100) as if it were the underlying index
// level (~₹24,000+), producing a margin ~200x too small (₹878.80 instead of a
// real ~₹2 lakh). Fail SAFE instead of fail permissive: report a number large
// enough that it can never be mistaken for "margin is basically free" and
// will correctly block a trade rather than silently under-margin it.
export const MARGIN_ESTIMATE_UNKNOWN_FLOOR = 9999999;

// Expiry-day extra margin (confirmed directly with Nikhil, Jul 2026):
// NSE/SEBI's documented rule is an extra 2% Extreme Loss Margin (ELM) on
// short index option positions on expiry day itself (effective 20 Nov 2024),
// applied even to hedged positions. Nikhil additionally confirmed — from his
// own broker's real behavior, not found in public SEBI/NSE documentation —
// that the window starts at exactly 2:00 PM IST on the trading day BEFORE
// expiry (T-1), continuing through all of expiry day (T).
export const EXPIRY_ELM_PCT = 0.02;
export const EXPIRY_MARGIN_CUTOFF_HOUR = 14; // 2:00 PM IST — ELM active from here on T-1

/** One leg of a margin-basket request (the /api/margin/basket shape). */
export interface MarginLeg {
  symbol: string;
  qty: number;
  side: "BUY" | "SELL";
  product?: string;
  ltp: number;
  underlying_ltp?: number | null;
  underlying?: string;
  expiry?: string;
}

/** True from 2:00 PM IST on the trading day before `expiryDateStr` (T-1)
 * through all of expiry day itself. `at` defaults per call to nowIST(). */
export function isExpiryElmActive(expiryDateStr: string | null | undefined, at?: Date): boolean {
  if (!expiryDateStr) return false;
  const ist = at ?? nowIST();
  const todayStr = dateStrFromLocalDate(ist);
  if (todayStr === expiryDateStr) return true; // all of expiry day itself
  const t1 = previousTradingDay(expiryDateStr);
  if (todayStr === t1) return ist.getHours() >= EXPIRY_MARGIN_CUTOFF_HOUR;
  return false;
}

/** Extra margin for a short index-option leg in the ELM window, computed
 * SEPARATELY from sellMarginEstimate's naked SPAN+Exposure figure so callers
 * can add this AFTER any hedge haircut — the real rule applies it in full
 * even when hedged, never diluted by the discount a genuine offsetting leg
 * would otherwise earn. Unknown underlying → 0 (decline to invent a
 * notional — the naked estimate's 9999999 floor already blocks the trade;
 * mirrors the backend's logged skip in _apply_expiry_elm). */
export function expiryMarginAddOn(
  underlyingLtp: number | null | undefined,
  qty: number,
  expiryDateStr: string | null | undefined,
  at?: Date,
): number {
  if (!underlyingLtp || !isExpiryElmActive(expiryDateStr, at)) return 0;
  return Math.abs(underlyingLtp) * Math.abs(qty) * EXPIRY_ELM_PCT;
}

/** Rough LOCAL-ONLY estimate of SPAN+Exposure margin for one naked short leg
 * — no hedge recognition (that needs the whole basket, which only the live
 * margin endpoint can actually see). NEVER substitute the option's own
 * premium for the underlying index level — if the real underlying price
 * isn't known, fail safe with the large placeholder rather than a
 * plausible-looking but wildly wrong small number. */
export function sellMarginEstimate(underlyingLtp: number | null | undefined, qty: number): number {
  if (!underlyingLtp) return MARGIN_ESTIMATE_UNKNOWN_FLOOR;
  const notional = Math.abs(underlyingLtp) * Math.abs(qty);
  return notional * (SPAN_ESTIMATE_PCT + EXPOSURE_ESTIMATE_PCT);
}

/** Nets legs by exact symbol (same strike + same optType, since expiry/
 * underlying are shared across one basket) BEFORE any margin/premium math.
 * A SELL and BUY of the literal same contract aren't a "hedge" that earns a
 * partial discount — they're a fully closed position with zero net exposure,
 * exactly like closing a trade by buying back what you sold. Treating them
 * as two separate legs (even with a hedge haircut applied) was the bug: it
 * produced a nonzero margin for a position that nets to nothing. Only
 * genuinely different strikes/types are real "hedge" legs. */
export function netBasketLegsBySymbol(legs: MarginLeg[]): MarginLeg[] {
  const bySymbol = new Map<string, {
    symbol: string; buyQty: number; sellQty: number; ltp: number;
    underlying_ltp?: number | null; underlying?: string; expiry?: string; product?: string;
  }>();
  for (const leg of legs) {
    if (!bySymbol.has(leg.symbol)) {
      // underlying_ltp must be carried through here too — dropping it silently reopens
      // the option-premium-as-underlying bug (see sellMarginEstimate) via this specific
      // basket-netting path, since a downstream fallback would see a leg with an ltp but
      // no underlying_ltp and might substitute one for the other.
      bySymbol.set(leg.symbol, { symbol: leg.symbol, buyQty: 0, sellQty: 0, ltp: leg.ltp, underlying_ltp: leg.underlying_ltp, underlying: leg.underlying, expiry: leg.expiry, product: leg.product });
    }
    const entry = bySymbol.get(leg.symbol)!;
    if (leg.side === "BUY") entry.buyQty += leg.qty; else entry.sellQty += leg.qty;
  }
  const netted: MarginLeg[] = [];
  for (const entry of bySymbol.values()) {
    const net = entry.buyQty - entry.sellQty;
    if (net === 0) continue; // fully offsetting — zero net exposure, contributes nothing
    netted.push({
      symbol: entry.symbol, side: net > 0 ? "BUY" : "SELL", qty: Math.abs(net),
      ltp: entry.ltp, underlying_ltp: entry.underlying_ltp, underlying: entry.underlying, expiry: entry.expiry, product: entry.product,
    });
  }
  return netted;
}

/** Local, hedge-aware fallback for a whole basket — same haircut/floor
 * constants as the backend's _local_margin_estimate, so an offline preview
 * is still in the right ballpark when the live call fails. Callers net the
 * legs first (netBasketLegsBySymbol). */
export function basketMarginEstimate(
  legsForApi: MarginLeg[],
  underlyingLtp: number | null | undefined,
  at?: Date,
): number {
  const shorts = legsForApi.filter(l => l.side === "SELL");
  const longs = legsForApi.filter(l => l.side === "BUY");
  let total = 0;
  for (const leg of shorts) {
    const naked = sellMarginEstimate(underlyingLtp, leg.qty);
    const hasHedge = longs.some(o => o.underlying === leg.underlying && o.expiry === leg.expiry);
    const hedged = hasHedge ? naked * (1 - HEDGE_HAIRCUT_ESTIMATE) : naked;
    const withFloor = Math.max(hedged, naked * SOMM_FLOOR_PCT_ESTIMATE);
    // Expiry-day ELM is NOT reduced by the hedge haircut above — applies in full
    // even on a hedged position, matching the real exchange rule Nikhil confirmed.
    total += withFloor + expiryMarginAddOn(underlyingLtp, leg.qty, leg.expiry, at);
  }
  for (const leg of longs) total += Math.abs(leg.ltp) * Math.abs(leg.qty);
  return total;
}
